import React, { useCallback, useMemo, useState } from 'react';
import {
  MRT_ColumnDef,
  MRT_ColumnFiltersState,
  MaterialReactTable,
  useMaterialReactTable,
} from 'material-react-table';
import {
  keepPreviousData,
  useQuery,
} from '@tanstack/react-query'
import { apiClient, apiClientHooks } from "../../bootstrapping/InitApiClient";
import { Helmet } from "react-helmet-async";
import { z } from 'zod';
import { schemas } from '../../generated/api/client';
import { isSomething } from '../../utils/utils';
import { isAxiosError } from 'axios';
import { useAppTable } from '../../components/AppTable';
import { getTableFilterSelectOptions, labelsForEBulkApplicationPaymentMethod, labelsForEBulkApplicationStatus, labelsForEmploymentType } from '../../constants/enums';
import { Box, Tooltip, Typography } from '@mui/material';
import IconButtonWithTooltip from '../../components/IconButtonWithTooltip';
import DeleteIcon from '@mui/icons-material/Delete';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import DoneOutlineIcon from '@mui/icons-material/DoneOutline';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate } from 'react-router-dom';
import RejectIconButton from '../../components/RejectIconButton';
import CompleteChangesIconButton from '../../components/CompleteChangesIconButton';
import TrackWithDBSIconButton from '../../components/TrackWithDBSIconButton';
import DBSTrackingStatusCell from '../../components/DBSTrackingStatusCell';
import EResultIconButton from '../../components/EResultIconButton';
import { doReloadAuth } from '../../utils/reloadAuth';
import { queryClient } from '../../bootstrapping/InitReactQuery';
import AutomatedVerificationButton from '../../components/AutomatedVerificationButton';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import { useProfileAssured } from '../../bootstrapping/InitRequireProfile';
import { getCustomerRole, useCaseWorkerContext } from '../../components/caseWorkers/caseWorkerContext';
import CaseWorkerAssignDialog from '../../components/caseWorkers/CaseWorkerAssignDialog';

type EBulkApplicationsWithMeta = z.infer<typeof schemas.ReturnType_GetEBulkApplications>
export type EBulkApplication = EBulkApplicationsWithMeta['rows'][number]



export const Element: React.FC = () => {
  const navigate = useNavigate()
  const profile = useProfileAssured()
  const { caseWorkersEnabled, selfEmployedAllowed } = useCaseWorkerContext()
  const [assignTarget, setAssignTarget] = useState<EBulkApplication | undefined>(undefined)

  const assignCaseWorker = async (application: EBulkApplication, caseWorkerProfileId: string | null) => {
    await apiClient.setCaseWorkerForEBulkApplicationByID(
      { case_worker_profile_id: caseWorkerProfileId },
      { params: { id: application.id } },
    )
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: apiClientHooks.getKeyByAlias('getEBulkApplications') }),
      // Assignment changes the dashboard's Case Worker section counts
      queryClient.invalidateQueries({ queryKey: apiClientHooks.getKeyByAlias('getGlobalStats') }),
    ])
  }

  // Fetch the status from the querystring (if present)
  const q_status = new URLSearchParams(window.location.search).get('status') ?? undefined
  const q_needs_isa = new URLSearchParams(window.location.search).get('needs_isa') ?? undefined
  const q_case_worker = new URLSearchParams(window.location.search).get('case_worker') ?? undefined
  const columnFilterInitialValues = useCallback((defaults: MRT_ColumnFiltersState) => [
    ...defaults,
    ...(
      q_status == null
        ? []
        : [{ id: 'status', value: q_status }]
    ),
    ...(
      q_needs_isa == null
        ? []
        : [
          { id: 'dbs_enumber_IS_NULL', value: 'false' },
          { id: 'dbs_isa_reference_IS_NULL', value: 'true' },
          { id: 'documents_customer_IS_ADULT_FIRST', value: 'true' },
        ]
    ),
    ...(
      q_case_worker == null
        ? []
        : [{ id: 'case_worker_profile_id', value: q_case_worker }]
    ),
  ], [q_status, q_needs_isa, q_case_worker])

  // Fetch the global filter from the querystring (if present)
  const q_global = new URLSearchParams(window.location.search).get('q') ?? undefined
  const globalFilterInitialValues = useCallback((defaults: string) => {
    return q_global ?? defaults
  }, [q_global])

  const {
    tableConfig,
  } = useAppTable<EBulkApplication, EBulkApplicationsWithMeta, Error | null>({
    columnFilterInitialValues,
    globalFilterInitialValues,
    useTableQuery: ({ columnFilters, globalFilter, pagination, sorting }) =>
      useQuery<EBulkApplicationsWithMeta>({
        queryKey: [
          ...apiClientHooks.getKeyByAlias('getEBulkApplications'),
          columnFilters, //refetch when columnFilters changes
          globalFilter ?? '', //refetch when globalFilter changes
          pagination.pageIndex, //refetch when pagination.pageIndex changes
          pagination.pageSize, //refetch when pagination.pageSize changes
          sorting, //refetch when sorting changes
        ],
        queryFn: async ({ queryKey }) => {
          // Build the sort by string, and ensure it meets the API schema
          const sort = (() => {
            const firstSort = sorting[0]
            if (!firstSort) return undefined

            const compoundSort = `${firstSort.id} ${firstSort.desc ? 'desc' : 'asc'}`
            return schemas.Sort_GetEBulkApplications.parse(compoundSort)
          })()

          try {
            const result = await apiClient.getEBulkApplications({
              queries: {
                page_index: `${pagination.pageIndex}`,
                page_size: `${pagination.pageSize}`,
                sort,
                globalFilter: globalFilter.length > 0 ? globalFilter : undefined,
                columnFilters: schemas.ColumnFilters_GetEBulkApplications.parse(
                  columnFilters
                    .map(filter => {
                      // Check ID is a valid column
                      const parseResult = schemas.ColumnFilters_GetEBulkApplications.element.safeParse(filter)
                      if (!parseResult.success && parseResult.error.errors.find(e => e.path[0] === 'id')) {
                        console.error("Invalid column ID for filter: ", filter)
                        return undefined
                      }

                      // Transform Enum Filters

                      // Transform Boolean Filters
                      if (filter.id === 'active' ||
                        filter.id === 'allowPayByInvoice' ||
                        filter.id === 'dbs_enumber_IS_NULL' ||
                        filter.id === 'dbs_isa_reference_IS_NULL' ||
                        filter.id === 'documents_customer_IS_ADULT_FIRST'
                      ) {
                        const valueAsStringBoolean = z.union([z.literal('true'), z.literal('false')]).safeParse(filter.value)
                        if (!valueAsStringBoolean.success) {
                          console.error("Invalid Boolean Filter (value should be string 'true' or 'false'): ", filter)
                          return undefined
                        }

                        return {
                          id: filter.id,
                          value: valueAsStringBoolean.data === 'true' ? true : false,
                        }
                      }

                      // Transform Date Range Filters
                      if (filter.id === 'createdAt' ||
                        filter.id === 'updatedAt' ||
                        filter.id === 'payment_at' ||
                        filter.id === 'data.DateOfBirth'
                      ) {
                        const values = z.array(z.date().optional()).safeParse(filter.value)
                        if (!values.success || values.data.length !== 2) {
                          console.error("Invalid date range filter: ", filter)
                          return undefined
                        }
                        if (values.data[0] === undefined && values.data[1] === undefined) {
                          // Nothing to filter on!
                          return undefined
                        }

                        return {
                          id: filter.id,
                          from: values.data[0]?.toISOString(),
                          to: values.data[1]?.toISOString(),
                        }
                      }

                      // Everything else is just a text filter
                      var parsedValue = z.string().safeParse(filter.value)
                      if (!parsedValue.success) {
                        console.error("Invalid text filter: ", filter)
                        return undefined
                      }

                      return {
                        id: filter.id,
                        value: parsedValue.data,
                      }
                    })
                    .filter(isSomething)
                )
              },
            })

            console.log("Applications.Element.queryFn: ", { result })
            return result
          } catch (error) {
            if (!isAxiosError(error)) {
              console.error("Error fetching applications (non-Axios): ", error)
              throw error
            }

            const parsedValue = schemas.ErrorResponse.safeParse(error.response?.data)
            if (!parsedValue.success) {
              console.error("Error fetching applications (unknown error): ", error)
              throw error
            }

            console.error("Error fetching applications: ", parsedValue.data.error)

            // Do we need to reload the auth tokens?
            if (['jwtExpired', 'errProfileJwtExpired'].includes(parsedValue.data.error.type ?? '')) {
              console.warn('Auth0 / Profile JWT Expired:', parsedValue.data.error);
              await doReloadAuth()
              await queryClient.invalidateQueries({ queryKey })
              return Promise.reject({ message: 'Auth tokens expired. Please refresh the data.' })
            }

            return Promise.reject(parsedValue.data.error)
          }
        },
        placeholderData: keepPreviousData, //don't go to 0 rows when refetching or paginating to next page
      }),
    columns: useMemo<MRT_ColumnDef<EBulkApplication>[]>(() => [
      {
        accessorKey: 'status',
        header: 'Status',
        size: 150,
        enableSorting: true,
        enableColumnFilter: true,
        Cell: ({ cell }) => labelsForEBulkApplicationStatus[cell.getValue() as keyof typeof labelsForEBulkApplicationStatus],
        filterVariant: 'select',
        filterSelectOptions: getTableFilterSelectOptions(labelsForEBulkApplicationStatus),
        _visible: q_status == null,
      },
      {
        accessorKey: 'rejection_reason',
        header: 'Rejection Reason',
        size: 250,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ cell, row }) => {
          const reason = cell.getValue() as EBulkApplication['rejection_reason']
          if (!reason) return ''
          const rejected_by = row.original.rejected_by
          const by = rejected_by === 'staff'
            ? 'Registered Body'
            : rejected_by === 'customer'
              ? 'your organisation'
              : rejected_by === 'applicant'
                ? 'the applicant'
                : rejected_by
          const text = by ? `${reason} — by ${by}` : reason
          return (
            <Tooltip title={text}>
              <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{text}</Box>
            </Tooltip>
          )
        },
        _visible: q_status === 'rejected',
      },
      {
        accessorKey: 'test_application',
        accessorFn: (originalRow) => (originalRow.test_application ? 'true' : 'false'), //must be strings
        header: 'Test Application',
        size: 150,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ cell }) => cell.getValue() === 'true' ? '✅' : '❌',
        _visible: false,
      },
      {
        accessorKey: 'documents_applicant',
        accessorFn: (originalRow) => (originalRow.documents_applicant != null ? 'true' : 'false'), //must be strings
        header: 'Documents Selected',
        size: 150,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ cell }) => cell.getValue() === 'true' ? '✅' : '❌',
        _visible: q_status === 'awaiting_customer_approval',
      },
      {
        accessorKey: 'profile_email',
        header: 'Email',
        size: 150,
        enableSorting: false,
        enableColumnFilter: true,
        _visible: q_status === 'draft',
      },
      {
        accessorKey: 'profile_given_name',
        header: 'Given Name',
        size: 150,
        enableSorting: true,
        enableColumnFilter: true,
      },
      {
        accessorKey: 'profile_family_name',
        header: 'Family Name',
        size: 150,
        enableSorting: true,
        enableColumnFilter: true,
      },
      {
        accessorKey: 'job_id',
        header: 'Job ID',
        size: 150,
        enableSorting: false,
        enableColumnFilter: true,
        _visible: false,
      },
      {
        accessorKey: 'job_role',
        header: 'Job Role',
        size: 150,
        enableSorting: true,
        enableColumnFilter: true,
      },
      // DBS-320: only defined at all when the customer allows self-employed applications
      ...(selfEmployedAllowed ? [
        {
          accessorKey: 'employment_type',
          header: 'Employment Type',
          size: 150,
          enableSorting: false,
          enableColumnFilter: true,
          Cell: ({ cell }) => labelsForEmploymentType[(cell.getValue() ?? 'employee') as keyof typeof labelsForEmploymentType],
          filterVariant: 'select',
          filterSelectOptions: getTableFilterSelectOptions(labelsForEmploymentType),
        },
      ] satisfies (MRT_ColumnDef<EBulkApplication> & { _visible?: boolean })[] : []),
      // Only defined at all when the customer manages case workers
      ...(caseWorkersEnabled === true ? [
        {
          accessorKey: 'case_worker_profile_id',
          header: 'Case Worker Profile ID',
          size: 150,
          enableSorting: false,
          enableColumnFilter: false,
          _visible: false,
        },
        {
          accessorKey: 'case_worker_name',
          header: 'Case Worker',
          size: 150,
          enableSorting: false,
          enableColumnFilter: false,
        },
      ] satisfies (MRT_ColumnDef<EBulkApplication> & { _visible?: boolean })[] : []),
      {
        accessorKey: 'payment_at',
        header: 'Payment At',
        size: 150,
        enableSorting: true,
        filterVariant: 'datetime-range',
        muiFilterDateTimePickerProps: {
          format: 'dd-MMM-yyyy HH:mm',
        },
        Cell: ({ cell }) => {
          const value = cell.getValue() as EBulkApplication['payment_at']
          return value ? new Date(value).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''
        },
        _visible: false,
      },
      {
        accessorKey: 'payment_method',
        header: 'Payment Method',
        size: 150,
        enableSorting: true,
        enableColumnFilter: true,
        Cell: ({ cell }) => labelsForEBulkApplicationPaymentMethod[cell.getValue() as keyof typeof labelsForEBulkApplicationPaymentMethod],
        filterVariant: 'select',
        filterSelectOptions: getTableFilterSelectOptions(labelsForEBulkApplicationPaymentMethod),
        _visible: false,
      },
      {
        accessorKey: 'payment_reference',
        header: 'Payment Reference',
        size: 150,
        enableSorting: true,
        enableColumnFilter: true,
        _visible: false,
      },
      {
        accessorKey: 'gdpr_consent',
        accessorFn: (originalRow) => (originalRow.gdpr_consent ? 'true' : 'false'), //must be strings
        header: 'GDPR Consent',
        size: 150,
        enableSorting: true,
        enableColumnFilter: true,
        Cell: ({ cell }) => cell.getValue() === 'true' ? '✅' : '❌',
        filterVariant: 'checkbox',
        _visible: false,
      },
      {
        accessorKey: 'dbs_enumber',
        header: 'E-Number',
        size: 150,
        enableSorting: false,
        enableColumnFilter: true,
        Cell: ({ cell }) =>
          <TrackWithDBSIconButton
            showENumber
            dbs_enumber={cell.row.original.dbs_enumber}
            DateOfBirth={cell.row.original.data.DateOfBirth}
            title="Track with DBS"
            icon={OpenInNewIcon}
            type='submit'
            color='primary'
          />,
        _visible: true,
      },
      {
        accessorKey: 'dbs_tracking',
        header: 'DBS Status',
        size: 140,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ cell }) =>
          <DBSTrackingStatusCell
            tracking={cell.row.original.dbs_tracking}
            dbs_enumber={cell.row.original.dbs_enumber}
          />,
        _visible: true,
      },
      {
        accessorKey: 'data.DateOfBirth',
        header: 'Date of Birth',
        size: 150,
        enableSorting: false,
        enableColumnFilter: true,
        filterVariant: 'datetime-range',
        muiFilterDateTimePickerProps: {
          format: 'dd-MMM-yyyy',
        },
        Cell: ({ cell }) => {
          const value = cell.getValue() as EBulkApplication['data']['DateOfBirth']
          return value ? new Date(value).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''
        },
        _visible: true,
      },
    ], [q_status, caseWorkersEnabled, selfEmployedAllowed]),
  })

  const table = useMaterialReactTable({
    ...tableConfig,
    enableRowActions: true,
    renderRowActions: ({ row }) => (
      <Box sx={{ display: 'flex', gap: '1rem' }}>
        {row.original.active === true
          ? <>
            {/* Managers can (re)assign the case worker while the application is open */}
            {caseWorkersEnabled === true
              && !['completed', 'rejected', 'restarted'].includes(row.original.status)
              && ['staff', 'manager'].includes(getCustomerRole(profile, row.original.customer_id) ?? '')
              && (
                <IconButtonWithTooltip
                  title="Assign case worker"
                  icon={AssignmentIndIcon}
                  color='primary'
                  onClick={() => setAssignTarget(row.original)}
                />
              )}
            {/* TODO: Create the RejectIconButton copy from WithdrawalButton in applicant  */}
            {/* If the Application has not been sent to the DBS yet (or errored from the DBS), show a button to reject the application */}
            {['draft', 'awaiting_customer_approval', 'awaiting_automated_verification', 'awaiting_payment', 'needs_changes', 'awaiting_countersigning', 'error_from_DBS'].includes(row.original.status) && (
              <RejectIconButton
                title="Reject"
                icon={DeleteIcon}
                color='error'
                dbsApp={row.original}
              />
            )}
            {/* If the Application is in the Customer Approval stage, show a button to approve the application (go to: `:id/meeting` relatively) */}
            {row.original.status === 'awaiting_customer_approval' && (
              <IconButtonWithTooltip
                title="Review Application"
                icon={HowToRegIcon}
                color='primary'
                onClick={() => { navigate(`${row.original.id}/meeting`) }}
              />
            )}
            {/* If the Application is in the Customer Approval stage and the Customer has it enabled, show a button to trigger automated verification) */}
            {row.original.status === 'awaiting_customer_approval' && (
              <AutomatedVerificationButton
                title="Automated Verification"
                icon={SmartToyIcon}
                color='success'
                dbsApp={row.original}
              />
            )}
            {/* If the Application is in the Awaiting Payment stage, show a button to approve the application (go to: `:id/payment` relatively) */}
            {row.original.status === 'awaiting_payment' && (
              <IconButtonWithTooltip
                title="Pay for Application"
                icon={ShoppingCartIcon}
                color='primary'
                onClick={() => { navigate(`${row.original.id}/payment`) }}
              />
            )}
            {/* If the Application is in the Needs Changes stage, show a button to push the application back into awaiting_countersigning */}
            {row.original.status === 'needs_changes' && (
              <CompleteChangesIconButton
                title="Return to Countersigning"
                icon={DoneOutlineIcon}
                color='primary'
                dbsApp={row.original}
              />
            )}
            {/* Show the E-Result */}
            {row.original.dbs_eresult != null && (
              <EResultIconButton
                title="Show E-Result"
                icon={WorkspacePremiumIcon}
                color='success'
                dbsApp={row.original}
              />
            )}
          </>
          : null}
      </Box>
    ),
  });

  return <>
    <Helmet>
      <title>Applications</title>
    </Helmet>
    <MaterialReactTable table={table} />;
    {assignTarget && assignTarget.customer_id && (
      <CaseWorkerAssignDialog
        open
        title="Assign case worker"
        description={`Choose the case worker for ${`${assignTarget.profile_given_name ?? ''} ${assignTarget.profile_family_name ?? ''}`.trim() || 'this application'}.`}
        customerId={assignTarget.customer_id}
        currentCaseWorkerProfileId={assignTarget.case_worker_profile_id}
        onClose={() => setAssignTarget(undefined)}
        onSubmit={caseWorkerProfileId => assignCaseWorker(assignTarget, caseWorkerProfileId)}
      />
    )}
  </>
}
